import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { Calendar } from "@phosphor-icons/react";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { format } from "date-fns";
import DateRangePickerErrorBoundaryRaw from "../../../DateRangePickerErrorBoundary";

// Cast to work around a duplicate @types/react install (via mui-one-time-password-input)
// that makes the class component's render() return type incompatible with JSX.ElementType.
const DateRangePickerErrorBoundary = DateRangePickerErrorBoundaryRaw as unknown as React.FC<{
  children?: React.ReactNode;
}>;

interface DateOfActivityPickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onDatesChange: (value: [Date | null, Date | null]) => void;
  noEndDate: boolean;
  onNoEndDateChange: (checked: boolean) => void;
  disableFuture?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

const formatValue = (startDate: Date | null, endDate: Date | null, noEndDate: boolean): string => {
  if (!startDate) return "";
  const start = format(startDate, "MM/dd/yyyy");
  if (noEndDate) return `${start} - Present`;
  if (endDate) return `${start} - ${format(endDate, "MM/dd/yyyy")}`;
  return start;
};

export const DateOfActivityPicker: React.FC<DateOfActivityPickerProps> = ({
  startDate,
  endDate,
  onDatesChange,
  noEndDate,
  onNoEndDateChange,
  disableFuture,
  disabled = false,
  error = false,
  helperText,
}) => {
  const [open, setOpen] = useState(false);

  const handleRangeChange = (value: [Date | null, Date | null]) => {
    const [start, end] = value;
    onDatesChange([start, noEndDate ? null : end]);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onNoEndDateChange(event.target.checked);
  };

  return (
    <Grid item xs={12}>
      <Stack gap={1}>
        <TextField
          id="date-of-activity"
          label="Date of activity"
          value={formatValue(startDate, endDate, noEndDate)}
          placeholder="MM/DD/YYYY"
          onClick={(event) => {
            if (!disabled) {
              // currentTarget here is TextField's root element, not the
              // input that actually holds focus.
              event.currentTarget.querySelector("input")?.blur();
              setOpen(true);
            }
          }}
          fullWidth
          disabled={disabled}
          error={error}
          helperText={helperText}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={(event) => {
                    event.currentTarget.blur();
                    setOpen(true);
                  }}
                  aria-label="Open date picker"
                  size="small"
                  edge="end"
                  disabled={disabled}
                >
                  <Calendar weight="duotone" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        scroll="paper"
        PaperProps={{
          "aria-label": "Date of activity date picker",
          sx: { width: 641, maxWidth: "calc(100vw - 32px)" },
        }}
      >
        <DateRangePickerErrorBoundary>
          <StaticDateRangePicker
            value={[startDate, endDate]}
            onChange={handleRangeChange}
            calendars={2}
            disableFuture={disableFuture}
            disabled={disabled}
            slotProps={{ actionBar: { actions: [] }, toolbar: { hidden: true } }}
          />
        </DateRangePickerErrorBoundary>
        <Divider />
        <Box sx={{ p: 5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={noEndDate}
                onChange={handleCheckboxChange}
                disabled={disabled}
              />
            }
            label="No end date (event history is active)"
          />
        </Box>
      </Dialog>
    </Grid>
  );
};

export default DateOfActivityPicker;
